export type WeaponCategory = 'ballistic' | 'energy' | 'rocket' | 'bomb' | 'melee';

export interface WeaponStatusEffect {
  type: 'burn' | 'slow' | 'stun';
  duration: number;       // seconds
  magnitude: number;      // burn: dps, slow: fraction (0.3 = -30%), stun: unused
}

export interface WeaponStats {
  damage: number;          // base damage per hit
  attackSpeed: number;     // seconds between attacks (lower = faster)
  range: number;           // tiles
  aoeRadius?: number;      // splash radius (rockets, bombs)
  energyCost?: number;     // energy per shot (energy weapons)
  burstCount?: number;     // shots per burst (ballistic)
  knockback?: number;      // push distance
  // Unique weapon behavior fields
  pierceCount?: number;       // bullets pass through N enemies (0=none)
  homingStrength?: number;    // turn rate in rad/sec for homing projectiles
  projectileCount?: number;   // fire N projectiles at once
  spreadAngle?: number;       // spread override (radians)
  speedMult?: number;         // projectile speed multiplier (default 1)
  fragmentCount?: number;     // spawn N fragment bullets on hit
  pullStrength?: number;      // reverse knockback — pull toward center
  lingerDuration?: number;    // seconds of lingering damage zone
  lingerDps?: number;         // damage per second of linger zone
  damageFalloff?: boolean;    // true = damage reduces center→edge
  doubleStrike?: boolean;     // melee hits twice
  bonusDamageMult?: number;   // bonus damage multiplier (e.g. 1.25 = +25%)
  splashRadius?: number;      // for energy: AOE splash around hitscan target
  statusEffect?: WeaponStatusEffect;
}

export interface WeaponDef {
  readonly kind: 'weapon';
  id: string;
  name: string;
  category: WeaponCategory;
  tier: number;            // 1-10 (tiers 6-10 only via forge-lv3 or deep loot)
  stats: WeaponStats;
  description: string;
}

// Helper to define weapons with kind field
function w(
  id: string, name: string, category: WeaponCategory, tier: number,
  stats: WeaponStats, description: string,
): WeaponDef {
  return { kind: 'weapon', id, name, category, tier, stats, description };
}

// ─── BALLISTIC ───────────────────────────────────────────
// High rate of fire, medium damage, medium range
const BALLISTIC: WeaponDef[] = [
  w('wpn_bal_1', 'Viper Rail-Autocannon',     'ballistic', 1,
    { damage: 4,  attackSpeed: 0.6,  range: 6.0, burstCount: 2, pierceCount: 1 },
    'Rapid-fire railgun with twin-burst mode. Punches through light plating.'),
  w('wpn_bal_2', 'Titan-Slug Bolter',         'ballistic', 2,
    { damage: 7,  attackSpeed: 0.8,  range: 7.0, burstCount: 1, knockback: 0.5 },
    'Fires dense titanium slugs with devastating stopping power.'),
  w('wpn_bal_3', 'Storm-Hail Rotary Gun',     'ballistic', 3,
    { damage: 5,  attackSpeed: 0.3,  range: 5.0, burstCount: 4, spreadAngle: 0.25 },
    'Six-barrel rotary gun unleashes a hailstorm of tungsten rounds.'),
  w('wpn_bal_4', 'Tungsten Needle-Driver',    'ballistic', 4,
    { damage: 10, attackSpeed: 0.5,  range: 9.0, burstCount: 3, pierceCount: 99, bonusDamageMult: 1.25 },
    'Hypersonic needle projectiles pierce multiple armor layers.'),
  w('wpn_bal_5', 'Hyper-Velocity Flak Cannon','ballistic', 5,
    { damage: 14, attackSpeed: 0.4,  range: 8.0, burstCount: 5, aoeRadius: 0.5, fragmentCount: 4 },
    'Fragmentation shells shatter into high-velocity shrapnel clouds.'),
  // ── Tier 6–10: forge-only evolutions (require Forge lv3). Stat ramp
  //    ~1.3× damage per tier with added features; fire rate keeps sliding
  //    downward until each shot feels like an event.
  w('wpn_bal_6', 'Coil-Mag Autoslayer',         'ballistic', 6,
    { damage: 19, attackSpeed: 0.38, range: 9.0, burstCount: 5, pierceCount: 2, aoeRadius: 0.5, fragmentCount: 4 },
    'Coil-magnetic autoloader spits penetrator bursts at terrifying cadence.'),
  w('wpn_bal_7', 'Gauss-Linear Overcannon',     'ballistic', 7,
    { damage: 25, attackSpeed: 0.36, range: 10.0, burstCount: 6, pierceCount: 3, aoeRadius: 0.6, fragmentCount: 5 },
    'Linear accelerator rails hurl depleted-matter slugs at relativistic speed.'),
  w('wpn_bal_8', 'Mass-Driver Heretic',         'ballistic', 8,
    { damage: 33, attackSpeed: 0.34, range: 11.0, burstCount: 7, pierceCount: 5, aoeRadius: 0.7, fragmentCount: 6, knockback: 0.4 },
    'Industrial mass-driver retooled for handheld atrocities.'),
  w('wpn_bal_9', 'Kessler-Swarm Repeater',      'ballistic', 9,
    { damage: 42, attackSpeed: 0.32, range: 12.0, burstCount: 8, pierceCount: 8, aoeRadius: 0.8, fragmentCount: 8, bonusDamageMult: 1.2 },
    'Fires a Kessler cascade of self-fragmenting kinetic payloads.'),
  w('wpn_bal_10', 'Omega-Nail Reality Spike',    'ballistic', 10,
    { damage: 55, attackSpeed: 0.30, range: 14.0, burstCount: 10, pierceCount: 99, aoeRadius: 1.0, fragmentCount: 10, bonusDamageMult: 1.5, knockback: 0.5 },
    'Drives monomolecular nails through the weave of local spacetime.'),
];

// ─── ENERGY ──────────────────────────────────────────────
// High damage, slower fire rate, long range. Energy beams pull straight
// from the capacitor as a hitscan strike — no per-shot drain (the
// capacitor's passive drain is the only running cost).
const ENERGY: WeaponDef[] = [
  w('wpn_ene_1', 'Nova-Beam Emitter',         'energy', 1,
    { damage: 6,  attackSpeed: 1.0,  range: 8.0,
      statusEffect: { type: 'burn', duration: 2, magnitude: 2 } },
    'Focused photon beam that melts through bulkheads.'),
  w('wpn_ene_2', 'Pulse-Arc Disintegrator',   'energy', 2,
    { damage: 10, attackSpeed: 1.2,  range: 9.0,
      statusEffect: { type: 'slow', duration: 2, magnitude: 0.2 } },
    'Arcing pulse blasts that scramble electronics on impact.'),
  w('wpn_ene_3', 'Singularity Lance',         'energy', 3,
    { damage: 15, attackSpeed: 1.5,  range: 11.0,
      pierceCount: 99 },
    'Concentrates gravitational energy into a piercing lance beam.'),
  w('wpn_ene_4', 'Neutron Flux-Ray',          'energy', 4,
    { damage: 20, attackSpeed: 1.8,  range: 12.0,
      bonusDamageMult: 1.3 },
    'Neutron radiation beam that ignores most shielding.'),
  w('wpn_ene_5', 'Gamma-Burst Scorch-Cannon', 'energy', 5,
    { damage: 28, attackSpeed: 2.0,  range: 14.0,
      splashRadius: 1.0, statusEffect: { type: 'burn', duration: 3, magnitude: 4 } },
    'Devastating gamma-ray burst vaporizes armor and circuits alike.'),
  w('wpn_ene_6', 'Quasar Blossom Lance',      'energy', 6,
    { damage: 38, attackSpeed: 1.8,  range: 15.0, pierceCount: 99,
      splashRadius: 1.2, statusEffect: { type: 'burn', duration: 3, magnitude: 5 } },
    'A captive quasar blooms along the beam path, scouring all in line.'),
  w('wpn_ene_7', 'Null-Collapse Beam',         'energy', 7,
    { damage: 52, attackSpeed: 1.7,  range: 16.0, pierceCount: 99,
      splashRadius: 1.5, bonusDamageMult: 1.25,
      statusEffect: { type: 'slow', duration: 3, magnitude: 0.35 } },
    'Collapses a localized null-field along the beam, erasing matter outright.'),
  w('wpn_ene_8', 'Cascade Annihilator',        'energy', 8,
    { damage: 68, attackSpeed: 1.6,  range: 17.0, pierceCount: 99,
      splashRadius: 1.8, bonusDamageMult: 1.3,
      statusEffect: { type: 'burn', duration: 4, magnitude: 6 } },
    'Chained annihilation reactions cascade through every target it touches.'),
  w('wpn_ene_9', 'Starforge Ion-Spear',        'energy', 9,
    { damage: 86, attackSpeed: 1.5,  range: 18.0, pierceCount: 99,
      splashRadius: 2.2, bonusDamageMult: 1.4,
      statusEffect: { type: 'burn', duration: 5, magnitude: 8 } },
    'Fragment of a starforge reaction core, discharged as a ruinous spear.'),
  w('wpn_ene_10', 'Eschaton Ray',               'energy', 10,
    { damage: 110, attackSpeed: 1.4, range: 20.0, pierceCount: 99,
      splashRadius: 2.6, bonusDamageMult: 1.6,
      statusEffect: { type: 'burn', duration: 6, magnitude: 10 } },
    'Unleashes a sliver of an ending — reality burns where it lands.'),
];

// ─── ROCKET ──────────────────────────────────────────────
// High damage, slow fire rate, long range, AoE
const ROCKET: WeaponDef[] = [
  w('wpn_rkt_1', 'Hydra Micro-Missile Pod',   'rocket', 1,
    { damage: 8,  attackSpeed: 1.5,  range: 10.0, aoeRadius: 0.8,
      projectileCount: 3, homingStrength: 1.5 },
    'Fires a spread of micro-missiles with basic heat tracking.'),
  w('wpn_rkt_2', 'Apex Predator Seeker',      'rocket', 2,
    { damage: 14, attackSpeed: 2.0,  range: 12.0, aoeRadius: 1.0,
      homingStrength: 4.0 },
    'Smart missile with neural-net target acquisition.'),
  w('wpn_rkt_3', 'Hellfire Swarm Launcher',   'rocket', 3,
    { damage: 18, attackSpeed: 2.5,  range: 13.0, aoeRadius: 1.5, knockback: 0.5,
      projectileCount: 2, statusEffect: { type: 'burn', duration: 3, magnitude: 3 } },
    'Unleashes a swarm of incendiary rockets that blanket an area.'),
  w('wpn_rkt_4', 'Javelin Kinetic Rocket',    'rocket', 4,
    { damage: 25, attackSpeed: 3.0,  range: 14.0, aoeRadius: 1.2, knockback: 0.8,
      speedMult: 2.0 },
    'Heavy kinetic penetrator with devastating impact force.'),
  w('wpn_rkt_5', 'Maelstrom Torpedo Rack',    'rocket', 5,
    { damage: 35, attackSpeed: 3.5,  range: 16.0, aoeRadius: 2.0, knockback: 1.2,
      lingerDuration: 3, lingerDps: 4 },
    'Full-size torpedoes that create localized destruction zones.'),
  w('wpn_rkt_6', 'Siege-Cluster Thunderhead',  'rocket', 6,
    { damage: 46, attackSpeed: 3.2,  range: 17.0, aoeRadius: 2.2, knockback: 1.3,
      lingerDuration: 3, lingerDps: 6, projectileCount: 2, homingStrength: 2.0 },
    'Cluster warhead peels apart mid-flight into guided thunder-heads.'),
  w('wpn_rkt_7', 'Voidburst Lance',            'rocket', 7,
    { damage: 60, attackSpeed: 3.0,  range: 18.0, aoeRadius: 2.5, knockback: 1.5,
      lingerDuration: 4, lingerDps: 8, homingStrength: 3.5, speedMult: 1.5 },
    'Voidburst payload collapses on contact — annihilation propagates outward.'),
  w('wpn_rkt_8', 'Hyperion Siege Battery',     'rocket', 8,
    { damage: 78, attackSpeed: 2.8,  range: 19.0, aoeRadius: 2.8, knockback: 1.7,
      lingerDuration: 4, lingerDps: 10, projectileCount: 3, homingStrength: 3.0 },
    'Mobile siege battery once used to level orbital strongholds.'),
  w('wpn_rkt_9', 'Apocalypse Seraphim Array',  'rocket', 9,
    { damage: 98, attackSpeed: 2.6,  range: 20.0, aoeRadius: 3.2, knockback: 1.8,
      lingerDuration: 5, lingerDps: 13, projectileCount: 4, homingStrength: 4.5 },
    'Self-directing seraph drones each carry a kiloton-yield warhead.'),
  w('wpn_rkt_10', 'Omega-Cluster World-Ender',  'rocket', 10,
    { damage: 125, attackSpeed: 2.4, range: 22.0, aoeRadius: 3.8, knockback: 2.0,
      lingerDuration: 6, lingerDps: 18, projectileCount: 5, homingStrength: 6.0, bonusDamageMult: 1.3 },
    'Prototype continent-cracker, rebuilt to fit in your silo.'),
];

// ─── BOMB ────────────────────────────────────────────────
// Very high AoE damage, very slow, short-medium range
const BOMB: WeaponDef[] = [
  w('wpn_bmb_1', 'Gravity-Well Imploder',     'bomb', 1,
    { damage: 12, attackSpeed: 3.0,  range: 5.0, aoeRadius: 1.5,
      pullStrength: 1.5 },
    'Creates a localized gravity anomaly that crushes everything nearby.'),
  w('wpn_bmb_2', 'Cryo-Static Freeze Charge', 'bomb', 2,
    { damage: 10, attackSpeed: 3.5,  range: 6.0, aoeRadius: 2.0,
      statusEffect: { type: 'slow', duration: 3, magnitude: 0.5 } },
    'Flash-freezes a wide area, shattering frozen targets.'),
  w('wpn_bmb_3', 'Napalm-X Incendiary Mine',  'bomb', 3,
    { damage: 20, attackSpeed: 4.0,  range: 4.0, aoeRadius: 2.5,
      statusEffect: { type: 'burn', duration: 4, magnitude: 3 } },
    'Deploys sticky napalm mines that coat surfaces in liquid fire.'),
  w('wpn_bmb_4', 'EMP Shockwave Burster',     'bomb', 4,
    { damage: 16, attackSpeed: 3.5,  range: 7.0, aoeRadius: 3.0,
      statusEffect: { type: 'stun', duration: 1, magnitude: 1 } },
    'Electromagnetic pulse disables all electronics in a wide radius.'),
  w('wpn_bmb_5', 'Anti-Matter Detonator',     'bomb', 5,
    { damage: 40, attackSpeed: 5.0,  range: 6.0, aoeRadius: 3.5,
      damageFalloff: true, knockback: 0.6 },
    'Controlled antimatter explosion annihilates everything in range.'),
  w('wpn_bmb_6', 'Gravitic Collapser Pod',     'bomb', 6,
    { damage: 54, attackSpeed: 4.5,  range: 7.0, aoeRadius: 4.0,
      damageFalloff: true, pullStrength: 2.5,
      statusEffect: { type: 'slow', duration: 2, magnitude: 0.4 } },
    'Pod collapses into a gravity well, then violently releases stored mass.'),
  w('wpn_bmb_7', 'Singularity Thumper',        'bomb', 7,
    { damage: 70, attackSpeed: 4.2,  range: 7.5, aoeRadius: 4.5,
      damageFalloff: true, pullStrength: 3.5,
      lingerDuration: 3, lingerDps: 10 },
    'Micro-singularity anchors mid-strike and pulses annihilation rings.'),
  w('wpn_bmb_8', 'Phase-Quench Cataclysm',     'bomb', 8,
    { damage: 90, attackSpeed: 4.0,  range: 8.0, aoeRadius: 5.0,
      damageFalloff: true, knockback: 0.8,
      statusEffect: { type: 'stun', duration: 1.5, magnitude: 1 } },
    'Quenches a local phase-field — everything inside flash-freezes in place.'),
  w('wpn_bmb_9', 'Cosmic-String Detonator',    'bomb', 9,
    { damage: 115, attackSpeed: 3.8, range: 8.5, aoeRadius: 5.5,
      damageFalloff: true, lingerDuration: 4, lingerDps: 14,
      statusEffect: { type: 'burn', duration: 4, magnitude: 6 } },
    'Taps a cosmic string and whips its release across the blast radius.'),
  w('wpn_bmb_10', 'End-of-Days Payload',        'bomb', 10,
    { damage: 150, attackSpeed: 3.6, range: 9.0, aoeRadius: 6.0,
      damageFalloff: true, knockback: 1.2, pullStrength: 2.5,
      lingerDuration: 5, lingerDps: 20, bonusDamageMult: 1.4 },
    'A last-resort device — nobody who deployed one ever filed a report.'),
];

// ─── MELEE ───────────────────────────────────────────────
// Very fast, high damage, very short range
const MELEE: WeaponDef[] = [
  w('wpn_mel_1', 'Monofilament Laser-Blade',  'melee', 1,
    { damage: 6,  attackSpeed: 0.4,  range: 1.5,
      bonusDamageMult: 1.25 },
    'Single-molecule edge slices through any material effortlessly.'),
  w('wpn_mel_2', 'Plasma-Edge War-Axe',       'melee', 2,
    { damage: 10, attackSpeed: 0.5,  range: 1.5, knockback: 0.3,
      statusEffect: { type: 'burn', duration: 3, magnitude: 2 } },
    'Plasma-sheathed axe head that cauterizes as it cleaves.'),
  w('wpn_mel_3', 'Kinetic Impact-Piston',     'melee', 3,
    { damage: 14, attackSpeed: 0.6,  range: 1.2, knockback: 0.6,
      statusEffect: { type: 'stun', duration: 0.5, magnitude: 1 } },
    'Hydraulic ram delivers bone-shattering punches at close range.'),
  w('wpn_mel_4', 'Tectonic Vibration-Maul',   'melee', 4,
    { damage: 20, attackSpeed: 0.7,  range: 1.5, knockback: 0.8, aoeRadius: 0.5 },
    'Seismic maul that sends shockwaves through the ground.'),
  w('wpn_mel_5', 'Thermal Chain-Saber',       'melee', 5,
    { damage: 26, attackSpeed: 0.45, range: 1.8, knockback: 0.4,
      doubleStrike: true, statusEffect: { type: 'burn', duration: 2, magnitude: 3 } },
    'Superheated chain-linked blade that rips through armor at blinding speed.'),
  w('wpn_mel_6', 'Plasma-Whip Devastator',     'melee', 6,
    { damage: 36, attackSpeed: 0.42, range: 2.2, knockback: 0.5, aoeRadius: 0.6,
      doubleStrike: true, statusEffect: { type: 'burn', duration: 3, magnitude: 4 } },
    'Flexible plasma filament lashes out in a wide burning arc.'),
  w('wpn_mel_7', 'Graviton Mace',              'melee', 7,
    { damage: 48, attackSpeed: 0.48, range: 2.0, knockback: 1.0, aoeRadius: 0.8,
      statusEffect: { type: 'stun', duration: 0.8, magnitude: 1 } },
    'Each swing drags a localized gravity well, crushing targets in its path.'),
  w('wpn_mel_8', 'Tachyon Edge',               'melee', 8,
    { damage: 62, attackSpeed: 0.38, range: 2.3, knockback: 0.6, aoeRadius: 0.6,
      doubleStrike: true, bonusDamageMult: 1.4,
      statusEffect: { type: 'burn', duration: 3, magnitude: 5 } },
    'Edge oscillates faster than light — cuts land before the swing completes.'),
  w('wpn_mel_9', 'Axiom-Cleaver',              'melee', 9,
    { damage: 80, attackSpeed: 0.42, range: 2.5, knockback: 1.2, aoeRadius: 1.0,
      doubleStrike: true, bonusDamageMult: 1.5,
      statusEffect: { type: 'stun', duration: 1.0, magnitude: 1 } },
    'Blade rewrites local axioms on contact — armor stops being armor.'),
  w('wpn_mel_10', 'Worldcleaver Monofil',      'melee', 10,
    { damage: 105, attackSpeed: 0.36, range: 2.8, knockback: 1.5, aoeRadius: 1.2,
      doubleStrike: true, bonusDamageMult: 1.8,
      statusEffect: { type: 'burn', duration: 4, magnitude: 8 } },
    'Monofilament edge wide enough to part continents, sheathed to one blade.'),
];

// ─── ALL WEAPONS ─────────────────────────────────────────
export const ALL_WEAPONS: WeaponDef[] = [
  ...BALLISTIC,
  ...ENERGY,
  ...ROCKET,
  ...BOMB,
  ...MELEE,
];

export const WEAPON_CATEGORIES: WeaponCategory[] = ['ballistic', 'energy', 'rocket', 'bomb', 'melee'];

export function getWeaponById(id: string): WeaponDef | undefined {
  return ALL_WEAPONS.find(w => w.id === id);
}

export function getWeaponsByCategory(category: WeaponCategory): WeaponDef[] {
  return ALL_WEAPONS.filter(w => w.category === category);
}

/**
 * Generate a random weapon drop based on distance from base.
 *
 * Tiers 1–5 follow a linear ramp (one tier per ~35 distance).
 * Tiers 6–10 are "mythic" — they're gated behind a far higher distance
 * and a second roll, so even deep runs only occasionally see them drop.
 * The rest of the game expects tier-6+ gear to come from the Forge lv3.
 */
export function generateWeaponLoot(distFromBase: number, dropChance: number = 0.3): WeaponDef | null {
  if (Math.random() > dropChance) return null;

  // Base ramp: tier 1 at dist 0 → tier 5 at dist ~140.
  const baseMax = Math.min(5, 1 + Math.floor(distFromBase / 35));
  const tierRoll = Math.random() * Math.random(); // squared → skews low
  let tier = Math.min(baseMax, 1 + Math.floor(tierRoll * baseMax));

  // Mythic bonus: only kicks in past dist 180, and even then a ≤4% roll.
  // Each successful roll bumps the tier by 1, capped at 10, so dist alone
  // never guarantees a mythic — the second roll has to cooperate too.
  if (baseMax >= 5 && distFromBase >= 180) {
    const mythicChance = Math.min(0.04, (distFromBase - 180) / 4000);
    while (tier < 10 && Math.random() < mythicChance) tier++;
  }

  const category = WEAPON_CATEGORIES[Math.floor(Math.random() * WEAPON_CATEGORIES.length)];
  const match = ALL_WEAPONS.find(w => w.category === category && w.tier === tier);
  return match ?? null;
}
